import React from 'react';

/**
 * StatusIndicator Component
 * Displays online status with colored lamps (green/orange/red)
 * 
 * @param {string} status - 'online', 'idle', or 'offline'
 * @param {string} responseTime - Expected response time
 * @param {string} size - 'sm', 'md', or 'lg'
 * @param {boolean} showText - Whether to show status text
 * @param {boolean} showResponseTime - Whether to show response time
 */
export default function StatusIndicator({ 
  status = 'offline', 
  responseTime = '', 
  size = 'md', 
  showText = true, 
  showResponseTime = false,
  className = '' 
}) {
  const getStatusConfig = () => {
    switch (status) {
      case 'online':
        return {
          color: 'bg-green-500',
          text: 'Online',
          textColor: 'text-green-600',
          bgColor: 'bg-green-50',
          borderColor: 'border-green-200',
          pulse: true
        };
      case 'idle':
      case 'away':
        return {
          color: 'bg-orange-500',
          text: 'Away',
          textColor: 'text-orange-600',
          bgColor: 'bg-orange-50',
          borderColor: 'border-orange-200',
          pulse: false
        };
      case 'busy': // ✅ FIX: Add busy status mapping
        return {
          color: 'bg-red-500',
          text: 'Busy',
          textColor: 'text-red-600',
          bgColor: 'bg-red-50',
          borderColor: 'border-red-200',
          pulse: false
        };
      case 'offline':
      default:
        return {
          color: 'bg-red-500',
          text: 'Offline',
          textColor: 'text-red-600',
          bgColor: 'bg-red-50',
          borderColor: 'border-red-200',
          pulse: false
        };
    }
  };

  const getSizeConfig = () => {
    switch (size) {
      case 'xs':
        return {
          lamp: 'w-1.5 h-1.5',
          text: 'text-xs',
          container: 'gap-1',
          padding: 'px-1.5 py-0.5'
        };
      case 'sm':
        return {
          lamp: 'w-2 h-2',
          text: 'text-xs',
          container: 'gap-1',
          padding: 'px-2 py-1'
        };
      case 'lg':
        return {
          lamp: 'w-4 h-4',
          text: 'text-sm',
          container: 'gap-2',
          padding: 'px-3 py-2'
        };
      case 'md':
      default:
        return {
          lamp: 'w-3 h-3',
          text: 'text-xs',
          container: 'gap-1.5',
          padding: 'px-2 py-1'
        };
    }
  };

  const statusConfig = getStatusConfig();
  const sizeConfig = getSizeConfig();

  if (!showText && !showResponseTime) {
    // Just the lamp indicator
    return (
      <div className={`relative ${className}`}>
        <div 
          className={`
            ${sizeConfig.lamp} 
            ${statusConfig.color} 
            rounded-full 
            ${statusConfig.pulse ? 'animate-pulse' : ''}
          `}
        />
        {statusConfig.pulse && (
          <div 
            className={`
              absolute inset-0 
              ${statusConfig.color} 
              rounded-full 
              animate-ping 
              opacity-75
            `}
          />
        )}
      </div>
    );
  }

  return (
    <div 
      className={`
        inline-flex items-center 
        ${sizeConfig.container} 
        ${sizeConfig.padding} 
        ${statusConfig.bgColor} 
        ${statusConfig.borderColor} 
        border 
        rounded-full 
        ${className}
      `}
    >
      {/* Status Lamp */}
      <div className="relative">
        <div 
          className={`
            ${sizeConfig.lamp} 
            ${statusConfig.color} 
            rounded-full
          `}
        />
        {statusConfig.pulse && (
          <div 
            className={`
              absolute inset-0 
              ${statusConfig.color} 
              rounded-full 
              animate-ping 
              opacity-75
            `}
          />
        )}
      </div>

      {/* Status Text */}
      {showText && (
        <span className={`${sizeConfig.text} font-medium ${statusConfig.textColor}`}>
          {statusConfig.text}
        </span>
      )}

      {/* Response Time */}
      {showResponseTime && responseTime && (
        <span className={`${sizeConfig.text} ${statusConfig.textColor} opacity-75`}>
          • {responseTime}
        </span>
      )}
    </div>
  );
}

// Export individual status components for convenience
export function OnlineIndicator(props) {
  return <StatusIndicator {...props} status="online" />;
}

export function IdleIndicator(props) {
  return <StatusIndicator {...props} status="idle" />;
}

export function OfflineIndicator(props) {
  return <StatusIndicator {...props} status="offline" />;
}
